import React, { useState } from "react";
import {
  Clock,
  Monitor,
  TrendingUp,
  Calendar,
  LayoutGrid,
  Lightbulb,
  Star,
  Plus,
  Minus,
  Check,
  ArrowRight,
} from "lucide-react";
import "./time-tracking-software.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";

const FEATURES = [
  {
    icon: Clock,
    title: "Built for Every Employee",
    body: "Everything you need from employee monitoring software and time tracking software designed for adoption and clarity.",
    tag: "Time tracking for employees",
  },
  {
    icon: Monitor,
    title: "Activity Monitoring",
    body: "Understand how work happens through employee monitoring software, application usage, focus time, and work pattern analysis using employee work monitoring software.",
    tag: "Time tracking software with screenshots",
  },
  {
    icon: TrendingUp,
    title: "Automatic Time Tracking",
    body: "Runs continuously with zero manual input, ensuring every billable minute is captured accurately with time tracker desktop app and time and attendance management.",
    tag: "Work tracking software",
  },
  {
    icon: Calendar,
    title: "AI Work Summaries",
    body: "Daily and weekly summaries showing what was worked on and where attention shifted through best employee monitoring software insights.",
    tag: "Employee management",
  },
  {
    icon: LayoutGrid,
    title: "Team & Project Visibility",
    body: "Track productivity across teams and projects to understand progress, capacity, and delivery trends with employee project time tracking.",
    tag: "Employee project time tracking",
  },
  {
    icon: Lightbulb,
    title: "Ask TeamGrid (AI)",
    body: "Get instant answers about overall team activity or individual performance using natural-language questions powered by tracking software for employees.",
    tag: "AI insights engine",
  },
  {
    icon: Lightbulb,
    title: "Organizational Intelligence)",
    body: "Detect risks and improvement areas by analyzing daily work patterns across the organization using desktop monitoring software and time tracking with screenshots.",
    tag: "AI insights engine",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "I have had a great experience in terms of productivity when using TeamGrid, as it has provided an effective solution for managing my team's productivity.",
    name: "Jay Mistry",
    role: "Founder, Craft Idea",
    initials: "JM",
  },
  {
    quote:
      "This has transformed how we understand our team's productivity. The insights are incredibly accurate.",
    name: "Rupesh Sanghavi",
    role: "CEO, Boson tech",
    initials: "RS",
  },
  {
    quote:
      "The real-time monitoring and predictive analytics have helped us improve our development velocity by 40%.",
    name: "Avnish Gevariya",
    role: "CMO, BrandGrid",
    initials: "AG",
  },
];

const SECURITY_BADGES = [
  { src: "/gartner.png", alt: "Gartner", label: "Gartner" },
  { src: "/g2_image.png", alt: "G2", label: "G2" },
  {
    src: "/product_hunt.png",
    alt: "Product Hunt",
    label: "Product Hunt",
  },
  { src: "/tech_imply.png", alt: "Tech Imply", label: "Tech Imply" },
  { src: "/saas_hub.png", alt: "SaaS Hub", label: "SaaS Hub" },
];

const TRUSTED_LOGOS = [
  { src: "/Brand_gridd.webp", alt: "BrandGrid", label: "BrandGrid" },
  { src: "/saeller_777.webp", alt: "Seller 7", label: "Seller 7" },
  {
    src: "/ecomsellertoollogo_.webp",
    alt: "Ecomsellertool",
    label: "Ecomsellertool",
  },
  { src: "/Cord4_11111.webp", alt: "CORD4", label: "CORD4" },
  { src: "/grow8_1.webp", alt: "grow 8", label: "" },
  { src: "/Container.png", alt: "Craft Idea", label: "Craft Idea" },
];

const FAQS = [
  {
    q: "What is TeamGrid AI Workforce Intelligence?",
    a: "TeamGrid is a tool that helps companies understand how work is getting done in employee monitoring software .Instead of only tracking activity, it shows useful insights to improve productivity, focus, and performance.",
  },
  {
    q: "Is TeamGrid just employee monitoring software?",
    a: "TeamGrid does include employee monitoring features, but it is more than that. It helps teams understand work patterns, problems, and productivity trends instead of only tracking what employees do.",
  },
  {
    q: "How does time tracking software for employees work in TeamGrid?",
    a: "TeamGrid automatically tracks work hours in the background or through projects. This makes time and attendance management simple and accurate, and it also connects time with tasks and results so teams can understand how work is being done.",
  },
];

const Logo = () => (
  <span className="mp-logo" aria-label="TeamGrid">
    <span className="mp-logo-mark" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
    TeamGrid
  </span>
);

const Stars = () => (
  <span className="mp-stars" aria-label="5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={16} fill="currentColor" stroke="currentColor" />
    ))}
  </span>
);

const TimeTrackingSoftware = () => {
  const [openIndex, setOpenIndex] = useState(-1);

  const navigate = useNavigate();
  const toggleFaq = (i) => setOpenIndex(openIndex === i ? -1 : i);

  const [demoForm, setDemoForm] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    size: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const recaptchaRef = React.useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const updateField = (key) => (e) => {
    const val =
      key === "phone" ?
        e.target.value.replace(/[^0-9]/g, "").slice(0, 10)
      : e.target.value;
    setDemoForm((s) => ({ ...s, [key]: val }));
    if (formErrors[key])
      setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();

    const fullName = (demoForm.fullName || "").trim();
    const company = (demoForm.company || "").trim();
    const email = (demoForm.email || "").trim();
    const phone = (demoForm.phone || "").trim();
    const employeeSize = (demoForm.size || "").trim();

    const errors = {};
    if (!fullName) errors.fullName = "Full Name is required.";
    if (!company || company.length < 2) {
      errors.company = "Company name is required (min 2 characters).";
    }
    if (!email) {
      errors.email = "Work Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!phone) {
      errors.phone = "Phone Number is required.";
    } else if (phone.length !== 10) {
      errors.phone = "Please enter a valid 10-digit phone number.";
    }
    if (!employeeSize) errors.size = "Please select an employee size.";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    setDemoSubmitting(true);
    try {
      const payload = {
        type: "contact_us",
        inquiryType: "Request A Demo",
        fullName,
        email,
        companyName: company,
        employeeSize,
        subject: "Request a Demo",
        message: `Demo request from marketing promo page. Phone: ${phone}${
          employeeSize ? `. Employee size: ${employeeSize}` : ""
        }`,
        recaptchaToken,
      };
      const response = await fetch("/api/v1/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Request failed (${response.status})`,
        );
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "form_submit",
        form_id: "demo",
        form_text: "Book Free Demo Now",
        form_url: window.location.href,
      });
      toast.success("Request received. We'll be in touch shortly.");
      setTimeout(() => {
        navigate("/thanks-demo-request", { state: { email } });
      }, 1000);
    } catch (err) {
      console.error("Demo request error:", err);
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setDemoSubmitting(false);
      setRecaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };

  return (
    <div className="mp-root">
      {/* ===== Navbar ===== */}
      <header className="mp-nav">
        <div className="mp-container mp-nav-inner">
          <div
            className="flex-shrink-0 flex items-center"
            // onClick={() => {
            //   navigate("/", {
            //     state: { showMainLanding: true },
            //     replace: true,
            //   });
            // }}
          >
            <img
              src="/logo.svg"
              alt="TeamGrid Logo"
              width={185}
              height={44}
              className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[185px] h-auto"
              style={{ objectFit: "contain" }}
            />
          </div>
          <div className="mp-nav-actions">
            <a href="#demo" className="mp-btn mp-btn-light">
              Book Demo
            </a>
            {/* <a href="#demo" className="mp-btn mp-btn-primary">
              Start Free Trial
            </a> */}
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="mp-hero">
        <div className="mp-container mp-hero-grid">
          <div>
            <span className="mp-eyebrow">
              ★ AI Workforce Intelligence Platform
            </span>
            <h1 className="mp-hero-title">
              The Best Employee
              <br />
              Monitoring and Time
              <br />
              Tracking Software
            </h1>
            <ul className="mp-hero-features" style={{ marginTop: "20px" }}>
              <li>Employee Activity Monitoring</li>
              <li>Automatic Time Tracking</li>
              <li>AI Work Summaries</li>
            </ul>
            <div className="mp-hero-ctas">
              <a href="#demo" className="mp-btn mp-btn-primary">
                Book a Demo
              </a>
              {/* <a href="#demo" className="mp-btn mp-btn-light">
                Start Free Trial
              </a> */}
            </div>
            <div className="mp-hero-trust">
              <span className="mp-hero-trust-item">
                <span className="mp-hero-trust-check">
                  <Check size={12} strokeWidth={3} />
                </span>
                No credit card required
              </span>
              {/* <span className="mp-hero-trust-item">
                <span className="mp-hero-trust-check">
                  <Check size={12} strokeWidth={3} />
                </span>
                5-day full-access trial
              </span> */}
            </div>
            <div className="mp-hero-stars">
              <Stars />
              <span>4.8/5 · 210+ reviews</span>
            </div>
          </div>

          {/* Demo form card */}
          <form
            id="demo"
            className="mp-form-card"
            onSubmit={handleDemoSubmit}
            noValidate
          >
            <div className="mp-form-head">
              <h3>Get a Free Demo Today</h3>
              {/* <span className="mp-badge-mint">5 days free</span> */}
            </div>
            <p className="mp-form-sub">
              Get full access to the workforce intelligence platform. Setup
              takes under 5 minutes.
            </p>

            <div className="mp-field">
              <label htmlFor="mp-full-name">
                Full name <span className="mp-required">*</span>
              </label>
              <input
                id="mp-full-name"
                className="mp-input"
                placeholder="Jordan Mehta"
                value={demoForm.fullName}
                onChange={updateField("fullName")}
                aria-invalid={!!formErrors.fullName}
                style={
                  formErrors.fullName ? { borderColor: "#EF4444" } : undefined
                }
              />
              {formErrors.fullName && (
                <span className="mp-field-error">{formErrors.fullName}</span>
              )}
            </div>

            <div className="mp-field">
              <label htmlFor="mp-company">
                Company name <span className="mp-required">*</span>
              </label>
              <input
                id="mp-company"
                className="mp-input"
                placeholder="Acme Inc."
                value={demoForm.company}
                onChange={updateField("company")}
                aria-invalid={!!formErrors.company}
                style={
                  formErrors.company ? { borderColor: "#EF4444" } : undefined
                }
              />
              {formErrors.company && (
                <span className="mp-field-error">{formErrors.company}</span>
              )}
            </div>

            <div className="mp-field">
              <label htmlFor="mp-email">
                Work email <span className="mp-required">*</span>
              </label>
              <input
                id="mp-email"
                className="mp-input"
                type="email"
                placeholder="jordan@acme.com"
                value={demoForm.email}
                onChange={updateField("email")}
                aria-invalid={!!formErrors.email}
                style={
                  formErrors.email ? { borderColor: "#EF4444" } : undefined
                }
              />
              {formErrors.email && (
                <span className="mp-field-error">{formErrors.email}</span>
              )}
            </div>

            <div className="mp-field-row">
              <div className="mp-field">
                <label htmlFor="mp-phone">
                  Phone number <span className="mp-required">*</span>
                </label>
                <input
                  id="mp-phone"
                  className="mp-input"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="+1 555 000 0000"
                  value={demoForm.phone}
                  onChange={updateField("phone")}
                  aria-invalid={!!formErrors.phone}
                  style={
                    formErrors.phone ? { borderColor: "#EF4444" } : undefined
                  }
                />
                {formErrors.phone && (
                  <span className="mp-field-error">{formErrors.phone}</span>
                )}
              </div>
              <div className="mp-field">
                <label htmlFor="mp-size">
                  Employee size <span className="mp-required">*</span>
                </label>
                <select
                  id="mp-size"
                  className="mp-select"
                  value={demoForm.size}
                  onChange={updateField("size")}
                  aria-invalid={!!formErrors.size}
                  style={
                    formErrors.size ? { borderColor: "#EF4444" } : undefined
                  }
                >
                  <option value="" disabled>
                    Select
                  </option>
                  <option>1 – 10</option>
                  <option>11 – 50</option>
                  <option>51 – 200</option>
                  <option>201 – 1000</option>
                  <option>1000+</option>
                </select>
                {formErrors.size && (
                  <span className="mp-field-error">{formErrors.size}</span>
                )}
              </div>
            </div>

            <div className="mp-recaptcha">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={(token) => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className="mp-btn mp-btn-primary mp-btn-block"
              disabled={demoSubmitting || !recaptchaToken}
            >
              {demoSubmitting ? "Submitting..." : "Book Free Demo Now"}{" "}
              <ArrowRight size={16} />
            </button>

            <p className="mp-form-fine">
              By submitting you agree to our <a href="#">Terms</a> &amp;{" "}
              <a href="#">Privacy Policy</a>. SOC 2 Type II · GDPR compliant.
            </p>
          </form>
        </div>
      </section>

      {/* ===== Trusted-by ===== */}
      <section className="mp-trusted">
        <div className="mp-container">
          <p className="mp-trusted-title">
            Trusted by 5,000+ HR teams, agencies, fintechs &amp; remote-first
            companies
          </p>
          <div className="mp-trusted-row">
            <div className="relative max-w-[1100px] mx-auto">
              <style>{`
    @keyframes scroll-left {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
    .logo-scroll {
      animation: scroll-left 30s linear infinite;
    }
    .logo-scroll:hover {
      animation-play-state: paused;
    }
  `}</style>
              <div className="flex overflow-hidden">
                <div className="flex logo-scroll gap-8 sm:gap-12 lg:gap-16 items-center whitespace-nowrap">
                  {(() => {
                    const logos = [
                      {
                        src: "/Brand_gridd.webp",
                        alt: "BrandGrid",
                        label: "BrandGrid",
                      },
                      {
                        src: "/saeller_777.webp",
                        alt: "Seller 7",
                        label: "Seller 7",
                      },
                      {
                        src: "/ecomsellertoollogo_.webp",
                        alt: "Ecomsellertool",
                        label: "Ecomsellertool",
                      },
                      {
                        src: "/Cord4_11111.webp",
                        alt: "CORD4",
                        label: "CORD4",
                      },
                      {
                        src: "/grow8_1.webp",
                        alt: "grow 8",
                        label: "",
                      },
                    ];
                    // Duplicate logos twice for seamless loop
                    return [...logos, ...logos].map((logo, index) => (
                      <div
                        key={`logo-${index}`}
                        className="flex-shrink-0 flex items-center gap-2"
                      >
                        <div
                          key={`logo-${index}`}
                          className="flex-shrink-0 flex items-center justify-center"
                        >
                          <img
                            src={logo.src}
                            alt={logo.alt}
                            className="h-6 sm:h-7 md:h-8 object-contain opacity-100 transition-opacity duration-300"
                          />
                        </div>
                        {/* <span className="text-[#232323] font-medium text-sm sm:text-base whitespace-nowrap">
                        {logo.label}
                      </span> */}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
            {/* {TRUSTED_LOGOS.map((logo) => (
              <img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className="mp-logo-img"
              />
            ))} */}
          </div>
        </div>
      </section>

      {/* ===== Dark CTA ===== */}
      <section className="mp-cta-dark">
        <div className="mp-cta-inner">
          {/* <div className="mp-cta-badges">
            <span className="mp-promo-badge">
              <span className="mp-promo-dot" />
              Limited: free onboarding + data migration for teams who start this
              quarter
            </span>
            <span className="mp-cta-pill-outline">
              Enterprise AI Workforce Intelligence
            </span>
          </div> */}
          <h2>
            Employee Monitoring Software that Shows Exactly How Your Team Works.
          </h2>
          <p>
            TeamGrid helps 5,000+ remote companies understand work patterns,
            improve productivity, and manage distributed teams with confidence.
            From time tracking to workforce insights — everything in one simple
            platform.
          </p>
          <div className="mp-cta-actions">
            <a href="#demo" className="mp-btn mp-btn-primary">
              Book a Demo
            </a>
            {/* <a href="#demo" className="mp-btn mp-btn-dark">
              Start Free Trial
            </a> */}
          </div>
          <p className="mp-cta-fine">
            No credit card required · 5-day full-access trial · Cancel anytime
          </p>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="mp-section">
        <div className="mp-container">
          <div className="mp-section-head">
            <span className="mp-eyebrow">One Platform</span>
            {/* <h2>
              Everything you need to{" "}
              <span className="mp-accent">
                manage
                <br />
                employees
              </span>
            </h2> */}
            <h2 className="mp-hero-title">
              Everything you need to
              <br />
              manage employees
            </h2>
            <p>
              A complete remote work tracking software suite from a time tracker
              desktop app to AI-powered workforce intelligence modules.
            </p>
          </div>

          <div className="mp-features-grid">
            {FEATURES.map((f, index) => {
              const Icon = f.icon;
              const isLastCentered = index === FEATURES.length - 1;
              return (
                <article
                  key={f.title}
                  className={`mp-feature-card${isLastCentered ? " mp-feature-card--center" : ""}`}
                >
                  <span className="mp-icon-tile">
                    <Icon size={26} strokeWidth={2.25} />
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                  <span className="mp-tag">{f.tag}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section className="mp-testimonials">
        <div className="mp-container">
          <div className="mp-section-head">
            <span className="mp-eyebrow">Customer Success</span>
            <h2 className="mp-hero-title">
              Why teams pick TeamGrid as their Employee Monitoring and Time
              <span className="mp-accent"> Tracking Software </span>
            </h2>
          </div>

          <div className="mp-testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <article key={t.name} className="mp-testimonial">
                <Stars />
                <p className="mp-testimonial-quote">{t.quote}</p>
                <div className="mp-testimonial-divider" />
                <div className="mp-testimonial-foot">
                  <span className="mp-avatar">{t.initials}</span>
                  <div>
                    <div className="mp-author-name">{t.name}</div>
                    <div className="mp-author-role">{t.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Security badges ===== */}
      <section className="mp-security">
        <div className="mp-container">
          <div className="mp-section-head" style={{ marginBottom: 0 }}>
            <span className="mp-eyebrow">Enterprise-grade Security</span>
            <h2>Compliant, secure &amp; built on trust</h2>
          </div>
          <div className="mp-security-row">
            {SECURITY_BADGES.map((b) => (
              <div key={b.alt} className="mp-badge-outline">
                <img src={b.src} alt={b.alt} className="mp-badge-img" />
                {/* <strong>{b}</strong> */}
                <Stars />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="mp-faq">
        <div className="mp-narrow">
          <div className="mp-section-head">
            <span className="mp-eyebrow">FAQ</span>
            <h2>
              Frequently Asked <span className="mp-accent">Questions</span>
            </h2>
            <p>
              Everything you need to know about how TeamGrid works and the value
              it delivers.
            </p>
          </div>

          <div className="mp-faq-list">
            {FAQS.map((f, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={f.q}
                  className={`mp-faq-item${isOpen ? " open" : ""}`}
                >
                  <button
                    className="mp-faq-q"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={isOpen}
                  >
                    {f.q}
                    <span className="mp-faq-toggle">
                      {isOpen ?
                        <Minus size={16} />
                      : <Plus size={16} />}
                    </span>
                  </button>
                  <div className="mp-faq-a">{f.a}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Sticky footer bar ===== */}
      <footer className="mp-footer-bar">
        <div className="mp-container mp-footer-inner">
          <div className="mp-footer-msg">
            Ready to see your workforce clearly?{" "}
            {/* <em>Start free – no card required.</em> */}
          </div>
          <div className="mp-footer-actions">
            <a href="#demo" className="mp-btn mp-btn-light">
              Book Demo
            </a>
            {/* <a href="#demo" className="mp-btn mp-btn-primary">
              Start Free Trial
            </a> */}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TimeTrackingSoftware;
